import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { ChangeDetectionStrategy, Component, forwardRef, OnDestroy, OnInit } from '@angular/core';
import { CommonStandaloneModule } from '../../../shared/common-standalone.module';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BannerComponent } from '@components';
import { LetDirective } from '@ngrx/component';
import { UIService } from '@services';
import { DashboardStore, DashboardTileOrder } from '@store';
import { ViCardComponent } from '../../../../projects/ui/components/card/card.component';
import { ViLoaderComponent } from '../../../../projects/ui/components/loader/loader.component';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DashboardTileNamePipe } from '../../pipes/dashboard-tile-name/dashboard-tile-name.pipe';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    forwardRef(() => CommonStandaloneModule),
    MatButtonModule,
    MatIconModule,
    FormsModule,
    LetDirective,
    MatCheckboxModule,
    DragDropModule,
    ViCardComponent,
    ViLoaderComponent,
    DashboardTileNamePipe,
    BannerComponent,
    MatTooltipModule,
  ],
  selector: 'app-dashboard-configuration-modal',
  standalone: true,
  styleUrls: ['./dashboard-configuration-modal.component.scss'],
  templateUrl: './dashboard-configuration-modal.component.html',
})
export class DashboardConfigurationModalComponent implements OnInit, OnDestroy {
  tiles: DashboardTileOrder[];
  initializedTilesOrder: DashboardTileOrder[];
  private dragAndDropPerformed = false;
  isSavingDisabled = false;

  private ngUnsubscribe$: Subject<void> = new Subject<void>();

  constructor(
    public dashboardStore: DashboardStore,
    public uiService: UIService,
    private dialogRef: MatDialogRef<DashboardConfigurationModalComponent>
  ) {}

  ngOnInit(): void {
    this.dashboardStore.tilesOrder$
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe((tilesOrder: DashboardTileOrder[]) => {
        this.tiles = structuredClone(tilesOrder);
        this.initializedTilesOrder = structuredClone(this.tiles);
      });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onSave(): void {
    this.dialogRef.close();
    if (this.hasTilesDataChanged() && !this.isSavingDisabled) {
      this.dashboardStore.saveTilesOrder(this.tiles, true, this.dragAndDropPerformed);
    }
  }

  drop(event: CdkDragDrop<DashboardTileOrder>): void {
    const prevTileIndex = event.previousIndex;
    const targetTileIndex = event.currentIndex;

    if (prevTileIndex === targetTileIndex) {
      return;
    }

    moveItemInArray(this.tiles, prevTileIndex, targetTileIndex);
    this.dragAndDropPerformed = true;
  }

  trackBy(_index: number, tile: DashboardTileOrder): string {
    return tile.type;
  }

  onCheckboxChange(): void {
    this.isSavingDisabled = !this.tiles.some((tile: DashboardTileOrder) => tile.visible);
  }

  private hasTilesDataChanged(): boolean {
    return JSON.stringify(this.initializedTilesOrder) !== JSON.stringify(this.tiles);
  }
}
